import React from 'react';

import { SRCSET_WIDTHS, buildImgixUrl, pickFallbackWidth } from 'Helpers/Image/image-urls';
import SkeletonHitCard from 'Components/Card/HitCard/SkeletonHitCard';
import SkeletonBlock from 'Components/Skeleton/SkeletonBlock';
import SkeletonImg from 'Components/Skeleton/SkeletonImg';
import SkeletonTakeOver from 'Components/Skeleton/SkeletonTakeOver';
import SkeletonCarouselBackground from 'Components/Skeleton/SkeletonCarouselBackground';

// The large card's `sizes` in CarouselCard (Components/Carousel/CardPanelCarousel)
const CAROUSEL_SIZES = '(min-width: 1520px) 646px, (min-width: 1200px) 550px, (min-width: 992px) 624px, (min-width: 768px) 704px, (min-width: 576px) 524px, 95vw';
const CAROUSEL_PX_WIDTHS = [646, 550, 624, 704, 524];

// The srcset FluidImg builds from CAROUSEL_SIZES: each px width at 1x and 2x, plus the ladder widths not within 15%
// of one. FluidImg also adds 95vw of the window at 1x and 2x, which isn't known at build time.
const carouselSources = (url) => {
  const sizesWidths = [...CAROUSEL_PX_WIDTHS, ...CAROUSEL_PX_WIDTHS.map(width => width * 2)];
  const isNear = ladder => sizesWidths.some(w => w / ladder > 0.85 && w / ladder < 1.15);
  const widths = [...sizesWidths, ...SRCSET_WIDTHS.filter(width => !isNear(width))]
    .sort((a, b) => b - a);
  const toUrl = width => buildImgixUrl(url, { width, fit: 'crop', height: Math.round(width / (16 / 9)) });

  return {
    srcSet: widths.map(width => `${toUrl(width)} ${width}w`).join(', '),
    src: toUrl(pickFallbackWidth(widths, Math.max(...CAROUSEL_PX_WIDTHS))),
  };
};

/**
 * Home page while it loads: the take over, the featured deals carousel's first panel (FeaturedCarouselContainer)
 * inside the carousel background campaign, the trust banner (md and up) and the top of Top Sellers
 * (TabbedTopSellers). The take over and the campaign each show only when live, and can show together.
 * Slots: `home-carousel` (image URL of the first panel's large card), plus SkeletonTakeOver's and
 * SkeletonCarouselBackground's.
 */
export default function HomePageSkeleton() {
  return (
    <div className="HomePageSkeleton">
      <SkeletonTakeOver />

      <div className="HomePageSkeleton__sections">
        <SkeletonCarouselBackground>
          <SkeletonBlock className="HomePageSkeleton__heading mb-3" />

          <div className="HomePageSkeleton__panel">
            <div className="HomePageSkeleton__main">
              <div className="SkeletonBlock HomePageSkeleton__large d-flex flex-column h-100">
                <div className="responsive-image responsive-image--16by9">
                  <SkeletonImg
                    slot="home-carousel"
                    aspectRatio={16 / 9}
                    sizes={CAROUSEL_SIZES}
                    sources={carouselSources}
                    className="img-force-full"
                  />
                </div>
                <div className="HomePageSkeleton__details flex-grow-1" />
              </div>
            </div>

            <div className="HomePageSkeleton__support">
              {[1, 2, 3, 4].map(card => (
                <div key={card} className={`SkeletonBlock HomePageSkeleton__small ${card > 2 ? 'd-none d-xl-block' : ''}`}>
                  <div className="responsive-image--16by9" />
                </div>
              ))}
            </div>
          </div>
        </SkeletonCarouselBackground>

        <section className="container d-none d-md-block">
          <SkeletonBlock className="HomePageSkeleton__trust" />
        </section>

        <section className="container">
          <div className="d-flex justify-content-between align-items-end mb-3">
            <SkeletonBlock className="HomePageSkeleton__heading" />
            <SkeletonBlock className="HomePageSkeleton__viewAll d-lg-none" />
          </div>

          <div className="d-flex mb-3">
            {[1, 2, 3, 4, 5].map(tab => (
              <SkeletonBlock key={tab} className={`HomePageSkeleton__tab mr-2 ${tab > 3 ? 'd-none d-md-block' : ''}`} />
            ))}
          </div>

          <div className="row">
            <SkeletonHitCard numberOfCards={1} xsSize={12} smSize={6} mdSize={6} lgSize={4} />
            <SkeletonHitCard numberOfCards={2} xsSize={6} smSize={6} mdSize={6} lgSize={4} />
          </div>
        </section>
      </div>
    </div>
  );
}
